import Cookie from "js-cookie";

export const GDPR_CONSENT_COOKIE_NAME = "appgrade_gdpr_consent";
export const GDPR_STAGING_CONSENT_COOKIE_NAME = "appgrade_staging_gdpr_consent";
export const GDPR_PREVIEW_CONSENT_COOKIE_NAME = "appgrade_preview_gdpr_consent";
export const GDPR_CONSENT_UPDATED_EVENT = "appgrade:gdpr-consent-updated";

export type GdprConsentSettings = {
    necessary: true;
    targetedAdvertising: boolean;
    personalization: boolean;
    analytics: boolean;
};

export const DEFAULT_GDPR_CONSENT_SETTINGS: GdprConsentSettings = {
    necessary: true,
    targetedAdvertising: false,
    personalization: false,
    analytics: false,
};

export const ESSENTIAL_GDPR_CONSENT_SETTINGS = DEFAULT_GDPR_CONSENT_SETTINGS;

export const ALL_GDPR_CONSENT_SETTINGS: GdprConsentSettings = {
    necessary: true,
    targetedAdvertising: true,
    personalization: true,
    analytics: true,
};

export const getGdprConsentCookieConfig = (hostname?: string) => {
    const currentHostname =
        hostname ?? (typeof window !== "undefined" ? window.location.hostname : "");

    if (
        currentHostname === "staging.appgrade.pro" ||
        currentHostname.endsWith(".staging.appgrade.pro")
    ) {
        return {
            name: GDPR_STAGING_CONSENT_COOKIE_NAME,
            domain: ".staging.appgrade.pro",
        };
    }

    if (
        currentHostname === "preview.appgrade.pro" ||
        currentHostname.endsWith(".preview.appgrade.pro")
    ) {
        return {
            name: GDPR_PREVIEW_CONSENT_COOKIE_NAME,
            domain: undefined,
        };
    }

    if (currentHostname === "appgrade.pro" || currentHostname.endsWith(".appgrade.pro")) {
        return {
            name: GDPR_CONSENT_COOKIE_NAME,
            domain: ".appgrade.pro",
        };
    }

    return {
        name: GDPR_CONSENT_COOKIE_NAME,
        domain: undefined,
    };
};

export const getGdprConsentSettings = () => {
    const { name } = getGdprConsentCookieConfig();
    const value = Cookie.get(name);

    if (!value) return null;

    try {
        const settings: unknown = JSON.parse(value);

        if (
            !settings ||
            typeof settings !== "object" ||
            !("necessary" in settings) ||
            settings.necessary !== true ||
            !("targetedAdvertising" in settings) ||
            typeof settings.targetedAdvertising !== "boolean" ||
            !("personalization" in settings) ||
            typeof settings.personalization !== "boolean" ||
            !("analytics" in settings) ||
            typeof settings.analytics !== "boolean"
        ) {
            return null;
        }

        return settings as GdprConsentSettings;
    } catch {
        return null;
    }
};

export const saveGdprConsentSettings = (settings: GdprConsentSettings) => {
    const { name, domain } = getGdprConsentCookieConfig();

    Cookie.set(name, JSON.stringify(settings), {
        domain,
        expires: 365,
        sameSite: "lax",
        secure: typeof window !== "undefined" && window.location.protocol === "https:",
    });

    window.dispatchEvent(new CustomEvent(GDPR_CONSENT_UPDATED_EVENT, { detail: settings }));
};

export const shouldRequestGdprConsent = () => {
    return getGdprConsentSettings() === null;
};

export const isGdprConsentCategoryAllowed = (
    category: keyof Pick<
        GdprConsentSettings,
        "targetedAdvertising" | "personalization" | "analytics"
    >,
) => {
    return getGdprConsentSettings()?.[category] === true;
};
